<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Box from '@uiKit/elements/Box.vue'
import Card from '@uiKit/components/Card.vue'
import DetailRow from '@uiKit/components/DetailRow.vue'
import InfoBanner from '@uiKit/components/InfoBanner.vue'
import PageNav from '@uiKit/components/PageNav.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import Text from '@uiKit/elements/Text.vue'

import { formatDateTime, formatKey, formatXrpFromDrops } from '@shared/utils'
import {
  peerAuthorizationColor,
  peerAuthorizationDetail,
  peerAuthorizationLabel,
} from '@shared/constants/trustlineAuthorization'
import { toErrorMessage } from '@core/api/normalizeError'
import { useAppToast } from '@shared/composables/useAppToast'

import { useWalletManagementDetailQuery } from '../api/queries/useWalletManagementDetailQuery'
import { useSetupTrustlineMutation } from '../api/mutations/useSetupTrustlineMutation'
import { useCheckTrustlineStatusMutation } from '../api/mutations/useCheckTrustlineStatusMutation'
import {
  cryptoWalletStatusColor,
  walletValidationMethodLabel,
} from '../constants/cryptoWalletStatus'
import { TRUSTLINE_STATUS } from '../constants/trustlineStatus'
import { buildTrustlineGaps } from '../utils/trustlineGaps'
import TrustlineGapList from '../components/TrustlineGapList.vue'
import TrustlinesTable from '../components/TrustlinesTable.vue'
import type { TrustlineGap } from '../types/cryptoWalletTypes'

const route = useRoute()
const router = useRouter()
const { showSuccess, showError, showInfo } = useAppToast()

const investorId = computed(() => String(route.params.investorId ?? ''))

// Set when an order redirected here, so the card the operator came for is the
// one outlined rather than one of several. Keyed by property because that is
// what a card is — one trustline, however many orders are waiting on it.
const highlightPropertyId = computed(() => String(route.query.propertyId ?? ''))

const { data, isLoading } = useWalletManagementDetailQuery(investorId)

const investor = computed(() => data.value?.investor)
const trustlines = computed(() => data.value?.trustlines ?? [])

// Both halves of the rule come from the one payload this screen already loads:
// the orders the VAISP has parked at the opt-in step, and what we hold in
// `investor_crypto_wallet_trustlines`. No order history is swept — a property is
// either a trustline this wallet is missing, or it is not this screen's problem.
const gaps = computed(() =>
  buildTrustlineGaps({
    pendingOptIns: data.value?.pendingOptIns ?? [],
    trustlines: trustlines.value,
  }),
)

// The ones an operator cannot clear from here: the offering carries no token to
// trust, so it has to be fixed upstream first. Counted in the banner so they
// know how much of the list is actually theirs; each card states its own reason.
const blockedGapCount = computed(() => gaps.value.filter((gap) => !gap.canSetupTrustline).length)

const gapBannerMessage = computed(() => {
  const total = gaps.value.length
  const subject = total === 1 ? '1 property' : `${total} properties`
  const lead = `${subject} cannot be delivered until this wallet agrees to hold the property token.`
  if (blockedGapCount.value === 0) return lead
  const blocked =
    blockedGapCount.value === 1
      ? '1 of them cannot be set up from here'
      : `${blockedGapCount.value} of them cannot be set up from here`
  return `${lead} ${blocked} — each card says why.`
})

// One action at a time, keyed by the gap's own key so only the card being acted
// on shows a spinner — a page-wide busy flag would freeze every other card too.
// Not `propertyId`: a gap grouped by name carries an empty one, which would
// match this ref's own empty initial value and spin forever.
const busyGapKey = ref('')

// ── XRP reserve (WAL-04) ────────────────────────────────────────────────────
// XRPL will not let a wallet open another trustline once its XRP balance falls
// below the base reserve plus one incremental reserve per existing line, so a
// short reserve is the silent cause of a TrustSet that never lands.
//
// The balance is integer DROPS as a string (1 XRP = 1,000,000 drops) and is
// never parsed into a number on the way here — `formatXrpFromDrops` converts by
// string arithmetic, because ledger amounts are int64 and a float round-trip
// drops the low-order digits.
const xrpReserveLabel = computed(() => {
  const drops = investor.value?.xrpBalanceDrops
  if (!drops) return 'Not read from the ledger'
  return `${formatXrpFromDrops(drops)} XRP`
})

// Shown so a figure read hours ago cannot be mistaken for the live balance —
// "Never read" is a distinct, meaningful state, not a missing value.
const xrpReserveReadLabel = computed(() =>
  investor.value?.xrpBalanceReadAt ? formatDateTime(investor.value.xrpBalanceReadAt) : 'Never read',
)

// Read-only mirror of the `investor_crypto_wallets` row. Empty values read as a
// dash so a missing field is visibly missing rather than a blank line.
const EMPTY_VALUE = '—'

const formatOptionalDate = (iso: string | undefined) => (iso ? formatDateTime(iso) : EMPTY_VALUE)

const primaryLabel = computed(() => {
  const isPrimary = investor.value?.isPrimary
  if (isPrimary === null || isPrimary === undefined) return EMPTY_VALUE
  return isPrimary ? 'Yes' : 'No'
})

const { mutate: setupTrustline } = useSetupTrustlineMutation()
const { mutate: checkTrustline } = useCheckTrustlineStatusMutation()

function handleSetup(gap: TrustlineGap) {
  busyGapKey.value = gap.key
  setupTrustline(
    { investorId: investorId.value, offeringId: gap.offeringId },
    {
      onSuccess: (trustline) => {
        // `pending` is a normal, frequent outcome: admin-service stops waiting
        // after ~15s and the ledger usually validates a few seconds later. Saying
        // "trustline set up" alone would leave the operator expecting the order
        // to move immediately.
        if (trustline.trustlineStatus === TRUSTLINE_STATUS.ACTIVE) {
          showSuccess(
            'Trustline active',
            `${gap.propertyName} can now be delivered to this wallet.`,
          )
          return
        }
        showInfo(
          'Trustline submitted',
          'The ledger accepted the transaction but has not validated it yet. Use “Check status” in a few seconds.',
        )
      },
      onError: (error: unknown) => {
        showError(
          'Trustline setup failed',
          toErrorMessage(error, 'An error occurred while setting up the trustline.'),
        )
      },
      onSettled: () => {
        busyGapKey.value = ''
      },
    },
  )
}

function handleCheck(gap: TrustlineGap) {
  busyGapKey.value = gap.key
  checkTrustline(
    { investorId: investorId.value, offeringId: gap.offeringId },
    {
      onSuccess: (trustline) => {
        if (trustline.trustlineStatus === TRUSTLINE_STATUS.ACTIVE) {
          // An active line is not necessarily a usable one: the issuer still has
          // to authorise it. Saying "active" alone had the operator expect the
          // order to move, so the toast reports the second axis too — and
          // reports an unread flag as unknown rather than as a refusal.
          showSuccess(
            'Trustline active',
            `The ledger has validated this trustline. Issuer authorisation: ${peerAuthorizationLabel(
              trustline.peerAuthorized,
            ).toLowerCase()}.`,
          )
          return
        }
        showInfo(
          'Still pending',
          'The ledger does not hold this trustline yet. The submitted transaction is still valid — check again shortly.',
        )
      },
      onError: (error: unknown) => {
        showError(
          'Status check failed',
          toErrorMessage(error, 'An error occurred while checking the trustline.'),
        )
      },
      onSettled: () => {
        busyGapKey.value = ''
      },
    },
  )
}

function goBack() {
  void router.push({ name: 'wallets.cryptoWallets.walletManagement' })
}

const breadcrumbs = computed(() => [
  { label: 'Wallet management', onClick: goBack },
  { label: investor.value?.investorName || investor.value?.email || 'Investor' },
])
</script>

<template>
  <Box flex direction="col" gap="4">
    <PageNav :crumbs="breadcrumbs" :on-back="goBack" />

    <InfoBanner
      v-if="gaps.length > 0"
      variant="warning"
      title="Orders are waiting on a trustline"
      :message="gapBannerMessage"
    />

    <!-- The server's own read-and-still-waiting verdict, which is a different
         claim from the per-line flag: it is only true when the ledger WAS read
         and the issuer has not authorised. An unread ledger never raises this
         banner, so the operator is never told to wait on something nobody has
         checked. Warning rather than danger — a wait is not a failure. -->
    <InfoBanner
      v-if="investor?.awaitingIssuerAuthorization"
      variant="warning"
      title="Waiting on issuer authorisation"
      message="The ledger has been read and Ctrl-Alt has not authorised this wallet yet. Nothing on this screen advances it — Ctrl-Alt authorises on its own schedule."
    />

    <!-- ── Custody wallet ── -->
    <Card variant="outlined" padding="4">
      <Box flex direction="col" gap="3">
        <Box flex justify="between" align="center" gap="2">
          <Text type="body" weight="medium">Custody wallet</Text>
          <!-- Two independent axes: is the wallet validated, and has the issuer
               authorised it. The second is three-state — a null reads "Not
               checked" in neutral, never as a refusal. -->
          <Box flex align="center" gap="2">
            <StatusBadge
              v-if="investor?.walletStatus"
              :color="cryptoWalletStatusColor(investor.walletStatus ?? undefined)"
              :label="formatKey(investor.walletStatus ?? '')"
            />
            <StatusBadge
              v-if="investor?.walletAddress"
              :color="peerAuthorizationColor(investor.peerAuthorized)"
              :label="peerAuthorizationLabel(investor.peerAuthorized)"
            />
          </Box>
        </Box>

        <!-- Read-only here. Creating the wallet is a Wallet management list
             action; setting up the first trustline also mints one. -->
        <Text v-if="!investor?.walletAddress" type="body-sm" color="muted">
          No custody wallet has been created yet. Create one from the Wallet management list.
        </Text>

        <Box v-else flex direction="col" gap="2">
          <DetailRow label="Wallet ID" :value="investor.walletId || EMPTY_VALUE" truncate-value />
          <DetailRow label="Address" :value="investor.walletAddress" truncate-value />
          <DetailRow label="Network" :value="investor.blockchainNetwork || EMPTY_VALUE" />
          <DetailRow
            label="Origin"
            :value="walletValidationMethodLabel(investor.validationMethod)"
          />
          <DetailRow
            label="Validation reference"
            :value="investor.validationReference || EMPTY_VALUE"
            truncate-value
          />
          <DetailRow label="Validated at" :value="formatOptionalDate(investor.validatedAt)" />
          <DetailRow
            label="VAISP wallet id"
            :value="investor.vaspReferenceId || 'Not registered'"
          />
          <DetailRow label="Primary wallet" :value="primaryLabel" />
          <!-- WAL-04. The reserve and the time it was read belong together: a
               balance with no read time cannot be judged for staleness, which is
               the only reason an operator looks at it. -->
          <DetailRow label="XRP reserve" :value="xrpReserveLabel" />
          <DetailRow label="Reserve read" :value="xrpReserveReadLabel" />
          <DetailRow label="Created at" :value="formatOptionalDate(investor.walletCreatedAt)" />
          <DetailRow label="Updated at" :value="formatOptionalDate(investor.walletUpdatedAt)" />
        </Box>

        <!-- The authorisation sentence, for the two states that need one. `info`
             in both: an unread ledger is not a problem and a wait on Ctrl-Alt is
             not a failure. Omitted once authorised — the badge says it. -->
        <InfoBanner
          v-if="investor?.walletAddress && investor.peerAuthorized !== true"
          variant="info"
          :title="peerAuthorizationLabel(investor.peerAuthorized)"
          :message="peerAuthorizationDetail(investor.peerAuthorized)"
        />
      </Box>
    </Card>

    <!-- ── Trustlines needed ── -->
    <Card variant="outlined" padding="4">
      <Box flex direction="col" gap="3">
        <Text type="body" weight="medium">Trustlines needed</Text>
        <Text type="caption" color="muted">
          Properties the VAISP is holding an order for at the opt-in step and this wallet has no
          working trustline record for. Where an order stands is on the orders screen.
        </Text>
        <TrustlineGapList
          :gaps="gaps"
          :busy-gap-key="busyGapKey"
          :highlight-property-id="highlightPropertyId"
          :loading="isLoading"
          @setup="handleSetup"
          @check="handleCheck"
        />
      </Box>
    </Card>

    <!-- ── All trustlines ── -->
    <Card variant="outlined" padding="0" class="table-card">
      <TrustlinesTable :trustlines="trustlines" :loading="isLoading" />
    </Card>
  </Box>
</template>
